import { useCallback, useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Badge, Button, Column, DataTable, EmptyState, Input, LongText, Modal, Select, useToast, NameCell } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { getSettingsRepository } from '../../../shared/services/settings/repositoryFactory';
import { ApplicationMember } from '../../../shared/services/settings/types';
import { RECORD_TYPE_LABEL, SHIFT_CLOSURE_EVENT_LABEL, ShiftClosureEvent, ShiftClosureLog } from '../../../shared/services/turnoGeo/types';
import { InfoTip } from './InfoTip';
import { formatMinutes, shortDateTime } from '../jornadaUtil';

const repo = getTurnoGeoRepository();
const settingsRepo = getSettingsRepository();

const EVENT_TONE: Record<ShiftClosureEvent, 'warning' | 'critical' | 'info'> = { CIERRE_EMPLEADO: 'warning', CIERRE_ADMIN: 'critical', CORRECCION_CIERRE: 'info' };
const REASON_LABEL: Record<string, string> = { MAX_HORAS: 'Superó las horas máximas', NUEVA_JORNADA: 'Empezó otra jornada' };
const startOfDayIso = (d: string) => new Date(`${d}T00:00:00`).toISOString();
const endOfDayIso = (d: string) => new Date(`${d}T23:59:59.999`).toISOString();

/**
 * Configuración > Turnos sin cerrar (solo administrador): histórico INMUTABLE de cada turno que quedó
 * abierto. Muestra cuándo entró, su última marcación, la hora sugerida y la hora correcta que informó
 * la persona, la nota obligatoria, quién lo cerró (o corrigió) y desde dónde. Se exporta a Excel.
 */
export function ShiftClosureHistoryTab() {
  const toast = useToast();
  const [members, setMembers] = useState<ApplicationMember[]>([]);
  const [rows, setRows] = useState<ShiftClosureLog[] | null>(null);
  const [userId, setUserId] = useState('');
  const [eventType, setEventType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [detail, setDetail] = useState<ShiftClosureLog | null>(null);

  const search = useCallback(() => {
    setRows(null);
    repo
      .listShiftClosureLog({
        userId: userId || undefined,
        eventType: (eventType || undefined) as ShiftClosureEvent | undefined,
        dateFrom: dateFrom ? startOfDayIso(dateFrom) : undefined,
        dateTo: dateTo ? endOfDayIso(dateTo) : undefined,
        limit: 500,
      })
      .then(setRows)
      .catch((err) => {
        setRows([]);
        toast.show(err instanceof Error ? err.message : 'No se pudo cargar el histórico.', 'error');
      });
  }, [userId, eventType, dateFrom, dateTo, toast]);

  useEffect(() => {
    settingsRepo.listApplicationMembers('turno-geo', true).then((m) => setMembers(m.filter((x) => x.hasAccess))).catch(() => setMembers([]));
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportExcel = () => {
    if (!rows || rows.length === 0) return;
    const data = rows.map((r) => ({
      'Registrado el': shortDateTime(r.createdAt),
      Evento: SHIFT_CLOSURE_EVENT_LABEL[r.eventType],
      Empleado: r.userFullName,
      Documento: r.userDocumentNumber ?? '',
      Entrada: shortDateTime(r.entryAt),
      'Última marcación': r.lastMarkType ? `${RECORD_TYPE_LABEL[r.lastMarkType]} ${r.lastMarkAt ? shortDateTime(r.lastMarkAt) : ''}` : '',
      'Salida programada': r.scheduledEndAt ? shortDateTime(r.scheduledEndAt) : '',
      'Hora sugerida': r.suggestedCloseAt ? shortDateTime(r.suggestedCloseAt) : '',
      'Hora anterior (corrección)': r.previousCloseAt ? shortDateTime(r.previousCloseAt) : '',
      'Hora de cierre correcta': shortDateTime(r.declaredCloseAt),
      'Duración del turno': formatMinutes(r.shiftMinutes),
      'Tiempo abierto en el sistema': formatMinutes(r.openMinutes),
      'Motivo del bloqueo': r.blockReason ? REASON_LABEL[r.blockReason] : '',
      Nota: r.note,
      'Hecho por': r.actorName,
      Rol: r.actorRole ?? '',
      IP: r.clientIp ?? '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Turnos sin cerrar');
    XLSX.writeFile(wb, `turnos_sin_cerrar_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columns: Column<ShiftClosureLog>[] = [
    {
      key: 'fecha', filterValue: (r) => r.createdAt.slice(0, 10),
      header: 'Registrado',
      sortable: true,
      sortValue: (r) => r.createdAt,
      render: (r) => (
        <div className="whitespace-nowrap">
          <p className="font-medium text-ink-800">{shortDateTime(r.createdAt)}</p>
          <Badge tone={EVENT_TONE[r.eventType]}>{SHIFT_CLOSURE_EVENT_LABEL[r.eventType]}</Badge>
        </div>
      ),
    },
    {
      key: 'empleado',
      header: 'Empleado',
      sortable: true,
      sortValue: (r) => r.userFullName,
      render: (r) => <NameCell name={r.userFullName} />,
    },
    { key: 'entrada', header: 'Entrada', render: (r) => <span className="whitespace-nowrap">{shortDateTime(r.entryAt)}</span> },
    {
      key: 'cierre',
      header: 'Cierre correcto',
      render: (r) => (
        <div className="whitespace-nowrap">
          <p className="font-semibold text-ink-800">{shortDateTime(r.declaredCloseAt)}</p>
          {r.previousCloseAt && <p className="text-xs text-ink-400">antes: {shortDateTime(r.previousCloseAt)}</p>}
          {!r.previousCloseAt && r.suggestedCloseAt && <p className="text-xs text-ink-400">sugerida: {shortDateTime(r.suggestedCloseAt)}</p>}
        </div>
      ),
    },
    { key: 'duracion', header: 'Turno / abierto', render: (r) => <span className="whitespace-nowrap text-xs">{formatMinutes(r.shiftMinutes)} · <span className="text-ink-400">abierto {formatMinutes(r.openMinutes)}</span></span> },
    { key: 'nota', header: 'Nota', render: (r) => <div className="max-w-[240px] text-xs text-ink-600"><LongText text={r.note} max={70} /></div> },
    { key: 'por', filterValue: (r) => r.actorName, header: 'Hecho por', render: (r) => <span className="text-xs">{r.actorName}</span> },
    { key: 'acc', header: '', render: (r) => <Button variant="outline" size="md" onClick={() => setDetail(r)}>Detalle</Button> },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-ink-100 bg-surface p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-ink-800">
          Histórico de turnos sin cerrar
          <InfoTip
            title="¿Qué guarda este histórico?"
            text="Cada vez que alguien deja un turno abierto y luego lo cierra (él mismo o un líder), queda aquí: la entrada, la última marcación, la salida programada, la hora sugerida, la hora correcta que informó y la nota obligatoria, quién lo hizo, desde qué IP y cuánto tiempo estuvo abierto. Si un líder corrige después esa hora de cierre, también queda. Este histórico no se puede editar ni borrar."
          />
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="Empleado" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Todos" options={members.map((m) => ({ value: m.id, label: m.fullName }))} />
          <Select
            label="Evento"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            placeholder="Todos"
            options={(Object.keys(SHIFT_CLOSURE_EVENT_LABEL) as ShiftClosureEvent[]).map((k) => ({ value: k, label: SHIFT_CLOSURE_EVENT_LABEL[k] }))}
          />
          <Input label="Desde" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <Input label="Hasta" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="md" onClick={search}>Buscar</Button>
          <Button size="md" variant="outline" onClick={exportExcel} disabled={!rows || rows.length === 0}>Exportar a Excel</Button>
          <span className="text-xs text-ink-400">{rows ? `${rows.length} evento(s).` : 'Cargando…'}</span>
        </div>
      </div>

      {rows && rows.length === 0 ? (
        <EmptyState icon="✅" title="Sin turnos sin cerrar" description="No hay cierres tardíos con esos filtros." />
      ) : (
        <DataTable excel fixedKey="empleado" columns={columns} rows={rows ?? []} rowKey={(r) => r.id} isLoading={rows === null} defaultSortKey="fecha" />
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Turno sin cerrar">
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={EVENT_TONE[detail.eventType]}>{SHIFT_CLOSURE_EVENT_LABEL[detail.eventType]}</Badge>
              {detail.blockReason && <Badge tone="neutral">{REASON_LABEL[detail.blockReason]}</Badge>}
            </div>
            <p className="font-semibold text-ink-800">{detail.userFullName}{detail.userDocumentNumber ? ` · CC ${detail.userDocumentNumber}` : ''}</p>
            {detail.cycleMarks && detail.cycleMarks.length > 0 && (
              <ul className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
                {detail.cycleMarks.map((m) => (
                  <li key={`${m.type}-${m.at}`} className="flex justify-between gap-3"><span>{RECORD_TYPE_LABEL[m.type]}</span><span className="tabular-nums">{shortDateTime(m.at)}</span></li>
                ))}
                <li className="flex justify-between gap-3 font-semibold text-critical"><span>Salida</span><span>no la marcó</span></li>
              </ul>
            )}
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-ink-400">Entrada</dt><dd>{shortDateTime(detail.entryAt)}</dd>
              {detail.scheduledEndAt && (<><dt className="text-ink-400">Salida programada</dt><dd>{shortDateTime(detail.scheduledEndAt)}</dd></>)}
              {detail.suggestedCloseAt && (<><dt className="text-ink-400">Hora sugerida</dt><dd>{shortDateTime(detail.suggestedCloseAt)}</dd></>)}
              {detail.previousCloseAt && (<><dt className="text-ink-400">Hora anterior</dt><dd>{shortDateTime(detail.previousCloseAt)}</dd></>)}
              <dt className="text-ink-400">Hora de cierre correcta</dt><dd className="font-semibold">{shortDateTime(detail.declaredCloseAt)}</dd>
              <dt className="text-ink-400">Duración del turno</dt><dd>{formatMinutes(detail.shiftMinutes)}</dd>
              <dt className="text-ink-400">Estuvo abierto</dt><dd>{formatMinutes(detail.openMinutes)}</dd>
              <dt className="text-ink-400">Hecho por</dt><dd>{detail.actorName}{detail.actorRole ? ` (${detail.actorRole})` : ''} · {shortDateTime(detail.createdAt)}</dd>
              {detail.clientIp && (<><dt className="text-ink-400">IP</dt><dd className="font-mono">{detail.clientIp}</dd></>)}
              {detail.userAgent && (<><dt className="text-ink-400">Navegador</dt><dd className="break-all text-ink-400">{detail.userAgent}</dd></>)}
            </dl>
            <div className="rounded-xl border border-ink-100 p-3 text-xs text-ink-700"><strong>Nota:</strong> {detail.note}</div>
            <Button variant="outline" fullWidth onClick={() => setDetail(null)}>Cerrar</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
